/**
 * Multi-wallet stack tracker — pure functions and the persisted shape.
 * NO React imports; the panel owns the hook, this owns the maths and the
 * validator that guards what comes back out of localStorage.
 */

import { SATS_PER_BTC, btcToSats } from './units';

/** One named holding. `btc` is always a finite, non-negative number. */
export interface Wallet {
  /** Stable across renders and reorders; used as the React key. */
  readonly id: string;
  readonly name: string;
  readonly btc: number;
}

export interface StackTotals {
  readonly walletCount: number;
  readonly totalBtc: number;
  readonly totalSats: number;
  /** null when no price is available — the stack is still countable in BTC. */
  readonly totalUsd: number | null;
}

/** Largest stack we accept, ~the supply cap. Blocks fat-finger paste errors. */
const MAX_BTC = 21_000_000;

export const MAX_WALLET_NAME = 24;

/**
 * Totals for the whole stack. Summing in satoshis (integers) and converting
 * once at the end avoids the float drift you get from adding 0.1 + 0.2 in BTC
 * across a dozen wallets and then showing 0.30000000000000004.
 */
export function stackTotals(wallets: readonly Wallet[], price: number | null): StackTotals {
  let sats = 0;
  for (const w of wallets) {
    if (Number.isFinite(w.btc) && w.btc > 0) sats += btcToSats(w.btc);
  }
  const totalBtc = sats / SATS_PER_BTC;
  return {
    walletCount: wallets.length,
    totalBtc,
    totalSats: sats,
    totalUsd: price !== null && Number.isFinite(price) && price > 0 ? totalBtc * price : null,
  };
}

/**
 * Parse a typed BTC amount. Accepts "0.5", ".5", "1,000.25" and a bare "".
 * Returns null for anything that is not a usable non-negative amount, so the
 * input can reject rather than silently storing NaN.
 */
export function parseBtcAmount(input: string): number | null {
  const cleaned = input.trim().replace(/,/g, '');
  if (cleaned === '') return 0;
  if (!/^\d*\.?\d*$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0 || n > MAX_BTC) return null;
  // Round to satoshi precision: sub-sat input is not representable on-chain.
  return btcToSats(n) / SATS_PER_BTC;
}

export function sanitizeWalletName(input: string): string {
  return input.replace(/\s+/g, ' ').trim().slice(0, MAX_WALLET_NAME);
}

let idCounter = 0;

/** Collision-safe without pulling in a uuid dependency. */
export function newWalletId(): string {
  idCounter += 1;
  return `w${Date.now().toString(36)}${idCounter.toString(36)}`;
}

export function createWallet(name: string, btc: number): Wallet {
  const clean = sanitizeWalletName(name);
  return {
    id: newWalletId(),
    name: clean === '' ? 'Wallet' : clean,
    btc: Number.isFinite(btc) && btc > 0 ? Math.min(btc, MAX_BTC) : 0,
  };
}

/** The wallets a first-time user sees. Zero balances, so nothing is invented. */
export function defaultWallets(): Wallet[] {
  return [createWallet('Cold storage', 0), createWallet('Exchange', 0)];
}

function isWallet(v: unknown): v is Wallet {
  if (typeof v !== 'object' || v === null) return false;
  const w = v as Record<string, unknown>;
  return (
    typeof w.id === 'string' &&
    w.id !== '' &&
    typeof w.name === 'string' &&
    typeof w.btc === 'number' &&
    Number.isFinite(w.btc) &&
    w.btc >= 0
  );
}

/**
 * Validator for `usePersistentState`. A stored blob that fails this is deleted
 * and replaced with the default — a hand-edited localStorage entry must not be
 * able to white-screen the dashboard.
 */
export function isWalletList(v: unknown): v is Wallet[] {
  return Array.isArray(v) && v.every(isWallet);
}

/* --- list operations, all returning new arrays ---------------------- */

export function addWallet(wallets: readonly Wallet[], name: string, btc: number): Wallet[] {
  return [...wallets, createWallet(name, btc)];
}

export function removeWallet(wallets: readonly Wallet[], id: string): Wallet[] {
  return wallets.filter((w) => w.id !== id);
}

export function updateWallet(
  wallets: readonly Wallet[],
  id: string,
  patch: Partial<Pick<Wallet, 'name' | 'btc'>>,
): Wallet[] {
  return wallets.map((w) =>
    w.id === id
      ? {
          ...w,
          ...(patch.name === undefined ? {} : { name: sanitizeWalletName(patch.name) }),
          ...(patch.btc === undefined ? {} : { btc: Math.max(0, Math.min(patch.btc, MAX_BTC)) }),
        }
      : w,
  );
}
